/**
 * Excel Export Utility
 * Handles Excel export with Arabic RTL support + DHR styling.
 *
 * Uses xlsx-js-style (drop-in replacement for xlsx) to apply cell-level
 * fonts, fills, borders, and alignment. The two libraries share the
 * same workbook/worksheet shape — only difference is the `.s` style
 * property on cells is honored on write.
 */
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { Platform } from 'react-native';

// DHR Excel theme — header tan/beige with white bold Cairo text, body
// in Cairo with thin borders. Tweaked to match the planning template
// the dept already prints with.
const HEADER_FILL = 'C9B481';
const BORDER_COLOR = '8C7A53';
const HEADER_FONT = { name: 'Cairo', bold: true, color: { rgb: 'FFFFFF' }, sz: 11 };
const BODY_FONT = { name: 'Cairo', sz: 10 };
const THIN_BORDER = {
  top: { style: 'thin', color: { rgb: BORDER_COLOR } },
  bottom: { style: 'thin', color: { rgb: BORDER_COLOR } },
  left: { style: 'thin', color: { rgb: BORDER_COLOR } },
  right: { style: 'thin', color: { rgb: BORDER_COLOR } },
};
const HEADER_CELL_STYLE = {
  font: HEADER_FONT,
  fill: { patternType: 'solid', fgColor: { rgb: HEADER_FILL } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true, readingOrder: 2 },
  border: THIN_BORDER,
};
const BODY_CELL_STYLE = {
  font: BODY_FONT,
  alignment: { horizontal: 'right', vertical: 'center', wrapText: true, readingOrder: 2 },
  border: THIN_BORDER,
};

/**
 * Apply DHR styling to every cell in the worksheet's used range.
 * Row 0 gets the header style, the rest get the body style.
 */
const applyDhrStyling = (worksheet: XLSX.WorkSheet): void => {
  const ref = worksheet['!ref'];
  if (!ref) return;
  const range = XLSX.utils.decode_range(ref);
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (!worksheet[addr]) {
        worksheet[addr] = { t: 's', v: '' };
      }
      worksheet[addr].s = r === 0 ? HEADER_CELL_STYLE : BODY_CELL_STYLE;
    }
  }
  // Freeze header row + bump default row heights for readability.
  worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };
  worksheet['!rows'] = [{ hpt: 32 }];
};

// Column configuration interface
interface ColumnConfig {
  header: string;      // Arabic header name
  key: string;         // Data key
  width?: number;      // Column width (in characters)
}

// Export options interface
interface ExportOptions {
  filename: string;
  sheetName?: string;
  columns: ColumnConfig[];
  data: any[];
  rtl?: boolean;       // Right-to-left (default: true for Arabic)
}

/**
 * Get nested value from object using dot notation
 * e.g., getNestedValue(obj, 'user.name') returns obj.user.name
 */
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : '';
  }, obj);
};

/**
 * Format value for Excel
 */
const formatValue = (value: any): string | number => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'boolean') {
    return value ? 'نعم' : 'لا';
  }
  if (value instanceof Date) {
    return value.toLocaleDateString('ar-AE');
  }
  if (typeof value === 'number') {
    return value;
  }
  return String(value);
};

/**
 * Calculate optimal column width for Arabic text
 * Arabic characters are typically wider than Latin characters
 */
const calculateColumnWidth = (header: string, data: any[], key: string, defaultWidth?: number): number => {
  if (defaultWidth) return defaultWidth;

  // Start with header width (Arabic chars need ~1.8x width)
  let maxWidth = header.length * 1.8;

  // Check data values
  data.forEach(item => {
    const value = getNestedValue(item, key);
    if (value !== null && value !== undefined) {
      const strValue = String(value);
      // Arabic text needs more width
      const isArabic = /[\u0600-\u06FF]/.test(strValue);
      const charWidth = isArabic ? 1.8 : 1;
      const valueWidth = strValue.length * charWidth;
      maxWidth = Math.max(maxWidth, valueWidth);
    }
  });

  // Clamp between reasonable bounds
  return Math.min(Math.max(maxWidth, 12), 60);
};

/**
 * Export data to Excel file with Arabic RTL support
 */
export const exportToExcel = ({
  filename,
  sheetName = 'Sheet1',
  columns,
  data,
  rtl = true,
}: ExportOptions): void => {
  try {
    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();

    // Prepare header row
    const headers = columns.map(col => col.header);

    // Prepare data rows
    const rows = data.map(item => {
      return columns.map(col => {
        const value = getNestedValue(item, col.key);
        return formatValue(value);
      });
    });

    // Combine headers and data
    const worksheetData = [headers, ...rows];

    // Create worksheet from array of arrays
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths with better Arabic support
    const colWidths = columns.map(col => ({
      wch: calculateColumnWidth(col.header, data, col.key, col.width)
    }));
    worksheet['!cols'] = colWidths;

    // Set RTL direction for Arabic - using the correct views approach
    if (rtl) {
      // Initialize views array if not present
      if (!worksheet['!views']) {
        worksheet['!views'] = [{}];
      }
      // Set rightToLeft property on the first view
      (worksheet['!views'] as any[])[0].rightToLeft = true;
    }

    // Apply DHR styling (Cairo font, tan header, borders, frozen row 1)
    applyDhrStyling(worksheet);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Set workbook properties for better Arabic support
    if (!workbook.Workbook) workbook.Workbook = {};
    if (!workbook.Workbook.Views) workbook.Workbook.Views = [{}];
    if (rtl) {
      (workbook.Workbook.Views[0] as any).RTL = true;
    }

    // Generate file and download
    if (Platform.OS === 'web') {
      // For web: use file-saver
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `${filename}.xlsx`);
    } else {
      // For mobile: would need expo-file-system or similar
      console.warn('Excel export on mobile requires additional setup');
    }
  } catch (error) {
    console.error('Excel export error:', error);
    throw error;
  }
};

/**
 * Create a worksheet from columns and data
 */
const createWorksheet = (columns: ColumnConfig[], data: any[], rtl = true): XLSX.WorkSheet => {
  const headers = columns.map(col => col.header);
  const rows = data.map(item =>
    columns.map(col => formatValue(getNestedValue(item, col.key)))
  );
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  worksheet['!cols'] = columns.map(col => ({
    wch: calculateColumnWidth(col.header, data, col.key, col.width)
  }));
  if (rtl) {
    if (!worksheet['!views']) worksheet['!views'] = [{}];
    (worksheet['!views'] as any[])[0].rightToLeft = true;
  }
  applyDhrStyling(worksheet);
  return worksheet;
};

/**
 * Export multiple sheets to a single Excel file
 */
interface SheetConfig {
  sheetName: string;
  columns: ColumnConfig[];
  data: any[];
}

export const exportMultiSheetToExcel = (filename: string, sheets: SheetConfig[], rtl = true): void => {
  try {
    const workbook = XLSX.utils.book_new();
    for (const sheet of sheets) {
      const worksheet = createWorksheet(sheet.columns, sheet.data, rtl);
      XLSX.utils.book_append_sheet(workbook, worksheet, sheet.sheetName);
    }
    if (!workbook.Workbook) workbook.Workbook = {};
    if (!workbook.Workbook.Views) workbook.Workbook.Views = [{}];
    if (rtl) {
      (workbook.Workbook.Views[0] as any).RTL = true;
    }
    if (Platform.OS === 'web') {
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, `${filename}.xlsx`);
    }
  } catch (error) {
    console.error('Excel export error:', error);
    throw error;
  }
};

// ============================================================
// Pre-configured export functions for different entities
// ============================================================

/**
 * Export Goals to Excel — DHR strategic-plan template.
 *
 * Row granularity: one row per strategic KPI. Goal-level fields repeat
 * across the rows that share a goal. Single-year columns (current year)
 * — the model only stores one target per KPI, so per-year columns
 * would be redundant.
 */
export const exportGoalsToExcel = (
  goals: any[],
  kpis: any[] = [],
  year: number = new Date().getFullYear(),
  filename = 'الأهداف_الاستراتيجية',
) => {
  const polarityLabel = (p: string) =>
    p === 'positive' ? 'التزايد أفضل' : p === 'negative' ? 'التناقص أفضل' : '';

  // Group strategic KPIs by their parent goal
  const kpisByGoal: Record<number, any[]> = {};
  kpis.forEach(kpi => {
    if (kpi.source_type && kpi.source_type !== 'strategic_goal') return;
    const id = kpi.strategic_goal;
    if (id == null) return;
    if (!kpisByGoal[id]) kpisByGoal[id] = [];
    kpisByGoal[id].push(kpi);
  });

  const rows: any[] = [];
  let rowNum = 0;
  goals.forEach(goal => {
    rowNum += 1;
    const goalKpis = kpisByGoal[goal.id] || [];
    const baseRow = {
      row_num: rowNum,
      emirate_plan_goal_title: goal.emirate_plan_goal_title || '',
      goal_title: goal.title || '',
      goal_description: goal.description || '',
      goal_weight: goal.weight ?? '',
    };
    if (goalKpis.length === 0) {
      rows.push({ ...baseRow });
      return;
    }
    goalKpis.forEach(kpi => {
      rows.push({
        ...baseRow,
        kpi_name: kpi.name || '',
        kpi_description: kpi.description || '',
        kpi_data_source: kpi.data_source || '',
        kpi_weight: kpi.weight ?? '',
        kpi_frequency_display: kpi.frequency_display || getFrequencyDisplay(kpi.frequency),
        kpi_measurement_formula: kpi.measurement_formula || '',
        kpi_responsible:
          kpi.organizational_unit_name ||
          kpi.owner ||
          kpi.department_name ||
          kpi.responsible_display ||
          '',
        kpi_polarity_display: polarityLabel(kpi.polarity),
        kpi_target_value: kpi.target_value ?? '',
        kpi_latest_value: kpi.latest_value ?? '',
      });
    });
  });

  exportToExcel({
    filename,
    sheetName: 'الأهداف الاستراتيجية',
    columns: [
      { header: '#', key: 'row_num', width: 5 },
      { header: 'هدف خطة الفجيرة', key: 'emirate_plan_goal_title', width: 30 },
      { header: 'اسم الهدف الإستراتيجي', key: 'goal_title', width: 35 },
      { header: 'وصف الهدف الإستراتيجي', key: 'goal_description', width: 45 },
      { header: 'الوزن النسبي للأهداف الإستراتيجية', key: 'goal_weight', width: 15 },
      { header: 'اسم المؤشر الإستراتيجي', key: 'kpi_name', width: 35 },
      { header: 'وصف المؤشر', key: 'kpi_description', width: 45 },
      { header: 'مصدر المؤشر', key: 'kpi_data_source', width: 25 },
      { header: 'الوزن النسبي للمؤشرات (%)', key: 'kpi_weight', width: 14 },
      { header: 'دورية القياس', key: 'kpi_frequency_display', width: 14 },
      { header: 'معادلة القياس', key: 'kpi_measurement_formula', width: 30 },
      { header: 'مسؤولية القياس', key: 'kpi_responsible', width: 20 },
      { header: 'القطعية', key: 'kpi_polarity_display', width: 14 },
      { header: `القيمة المستهدفة ${year}`, key: 'kpi_target_value', width: 15 },
      { header: `القيمة المحققة ${year}`, key: 'kpi_latest_value', width: 15 },
    ],
    data: rows,
  });
};

/**
 * Export Initiatives to Excel — DTS chapter 8 layout.
 *
 * Row granularity: one row per operational KPI. Initiative-level fields
 * (goal, dates, budget, risks) repeat across the rows that share an
 * initiative — same shape as the official DHR planning template.
 *
 * KPIs without any reading produce a row with empty indicator columns;
 * risks are joined per initiative (first match wins — DTS template has
 * only one risk slot per row).
 */
export const exportInitiativesToExcel = (
  initiatives: any[],
  kpis: any[] = [],
  risks: any[] = [],
  filename = 'المبادرات',
) => {
  const kpisByInitiative: Record<number, any[]> = {};
  kpis.forEach(kpi => {
    const id = kpi.initiative_id ?? kpi.initiative;
    if (id == null) return;
    if (!kpisByInitiative[id]) kpisByInitiative[id] = [];
    kpisByInitiative[id].push(kpi);
  });

  const risksByInitiative: Record<number, any[]> = {};
  risks.forEach(risk => {
    if (risk.related_type !== 'initiative') return;
    const id = risk.related_id;
    if (!risksByInitiative[id]) risksByInitiative[id] = [];
    risksByInitiative[id].push(risk);
  });

  const polarityLabel = (p: string) =>
    p === 'positive' ? 'التزايد أفضل' : p === 'negative' ? 'التناقص أفضل' : '';

  const rows: any[] = [];
  initiatives.forEach(init => {
    const initKpis = kpisByInitiative[init.id] || [];
    const initRisks = risksByInitiative[init.id] || [];
    const risk = initRisks[0] || {};

    const baseRow = {
      strategic_goal_code: init.strategic_goal_code || '',
      strategic_goal_title: init.strategic_goal_title || '',
      initiative_type_display: init.initiative_type_display || '',
      title: init.title || '',
      responsible_units_names: init.responsible_units_names || init.department_name || '',
      start_date: init.start_date || '',
      end_date: init.end_date || '',
      budget: init.budget ?? '',
      expected_impact: risk.expected_impact || '',
      probability: risk.probability ?? '',
      impact: risk.impact ?? '',
    };

    if (initKpis.length === 0) {
      rows.push({ ...baseRow });
      return;
    }
    initKpis.forEach(kpi => {
      rows.push({
        ...baseRow,
        kpi_name: kpi.name || '',
        kpi_unit: kpi.unit || '',
        kpi_target_value: kpi.target_value ?? '',
        kpi_latest_value: kpi.latest_value ?? '',
        kpi_measurement_formula: kpi.measurement_formula || '',
        kpi_frequency_display: kpi.frequency_display || getFrequencyDisplay(kpi.frequency),
        kpi_polarity_display: polarityLabel(kpi.polarity),
        kpi_responsible:
          kpi.organizational_unit_name ||
          kpi.owner ||
          kpi.department_name ||
          kpi.responsible_display ||
          '',
      });
    });
  });

  exportToExcel({
    filename,
    sheetName: 'العناصر التشغيلية',
    columns: [
      { header: 'رقم الهدف الإستراتيجي', key: 'strategic_goal_code', width: 18 },
      { header: 'اسم الهدف الإستراتيجي', key: 'strategic_goal_title', width: 30 },
      { header: 'نوع العنصر', key: 'initiative_type_display', width: 15 },
      { header: 'اسم العنصر التشغيلي', key: 'title', width: 35 },
      { header: 'الوحدة التنظيمية المسؤولة', key: 'responsible_units_names', width: 25 },
      { header: 'تاريخ البداية', key: 'start_date', width: 14 },
      { header: 'تاريخ النهاية', key: 'end_date', width: 14 },
      { header: 'الميزانية', key: 'budget', width: 14 },
      { header: 'الخطر المتوقع', key: 'expected_impact', width: 30 },
      { header: 'الاحتمالية (1-5)', key: 'probability', width: 12 },
      { header: 'التأثير (1-5)', key: 'impact', width: 12 },
      { header: 'اسم المؤشر التشغيلي', key: 'kpi_name', width: 35 },
      { header: 'وحدة القياس', key: 'kpi_unit', width: 14 },
      { header: 'القيمة المستهدفة', key: 'kpi_target_value', width: 15 },
      { header: 'القيمة المحققة', key: 'kpi_latest_value', width: 15 },
      { header: 'معادلة القياس', key: 'kpi_measurement_formula', width: 30 },
      { header: 'دورية القياس', key: 'kpi_frequency_display', width: 14 },
      { header: 'القطعية', key: 'kpi_polarity_display', width: 14 },
      { header: 'مسؤولية القياس', key: 'kpi_responsible', width: 20 },
    ],
    data: rows,
  });
};

/**
 * Export KPIs to Excel
 */
export const exportKPIsToExcel = (kpis: any[], filename = 'مؤشرات_الأداء') => {
  exportToExcel({
    filename,
    sheetName: 'المؤشرات',
    columns: [
      { header: 'اسم المؤشر', key: 'name', width: 40 },
      { header: 'الوصف', key: 'description', width: 50 },
      { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
      { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
      { header: 'القيمة المحققة', key: 'achieved_value', width: 15 },
      { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
      { header: 'وحدة القياس', key: 'unit', width: 15 },
      { header: 'التكرار', key: 'frequency_display', width: 15 },
      { header: 'الاتجاه', key: 'polarity_display', width: 15 },
      { header: 'الوزن', key: 'weight', width: 10 },
    ],
    data: kpis.map(kpi => ({
      ...kpi,
      frequency_display: getFrequencyDisplay(kpi.frequency),
      polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
    })),
  });
};

/**
 * Export KPI Readings to Excel
 */
export const exportKPIReadingsToExcel = (readings: any[], kpiName: string, filename = 'قراءات_المؤشر') => {
  exportToExcel({
    filename: `${filename}_${kpiName}`,
    sheetName: 'القراءات',
    columns: [
      { header: 'تاريخ القياس', key: 'measurement_date', width: 15 },
      { header: 'القيمة', key: 'value', width: 15 },
      { header: 'المستهدف', key: 'target_value', width: 15 },
      { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
      { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
      { header: 'الملاحظات', key: 'notes', width: 40 },
      { header: 'تاريخ الإدخال', key: 'created_at', width: 15 },
    ],
    data: readings,
  });
};

/**
 * Export Employees to Excel
 */
export const exportEmployeesToExcel = (employees: any[], filename = 'الموظفين') => {
  exportToExcel({
    filename,
    sheetName: 'الموظفين',
    columns: [
      { header: 'الاسم', key: 'name', width: 25 },
      { header: 'الاسم بالعربية', key: 'name_ar', width: 25 },
      { header: 'البريد الإلكتروني', key: 'email', width: 30 },
      { header: 'رقم الهاتف', key: 'phone', width: 15 },
      { header: 'المسمى الوظيفي', key: 'job_title', width: 25 },
      { header: 'الوحدة التنظيمية', key: 'organizational_unit_name', width: 25 },
      { header: 'الحالة', key: 'status_display', width: 15 },
    ],
    data: employees.map(emp => ({
      ...emp,
      status_display: emp.is_active ? 'نشط' : 'غير نشط',
    })),
  });
};

/**
 * Export Organizations Overview to Excel
 */
export const exportOrganizationsToExcel = (organizations: any[], filename = 'الجهات') => {
  exportToExcel({
    filename,
    sheetName: 'الجهات',
    columns: [
      { header: 'اسم الجهة', key: 'name', width: 30 },
      { header: 'عدد الأهداف', key: 'total_goals', width: 15 },
      { header: 'عدد المبادرات', key: 'total_initiatives', width: 15 },
      { header: 'عدد المؤشرات', key: 'total_kpis', width: 15 },
      { header: 'مؤشرات جيدة', key: 'kpis_good', width: 15 },
      { header: 'مؤشرات متوسطة', key: 'kpis_average', width: 15 },
      { header: 'مؤشرات ضعيفة', key: 'kpis_poor', width: 15 },
      { header: 'نسبة تحقيق الرؤية', key: 'vision_achievement', width: 18 },
      { header: 'مؤشر الالتزام', key: 'commitment_index', width: 15 },
    ],
    data: organizations,
  });
};

/**
 * Export Services to Excel
 */
export const exportServicesToExcel = (services: any[], filename = 'الخدمات') => {
  exportToExcel({
    filename,
    sheetName: 'الخدمات',
    columns: [
      { header: 'اسم الخدمة', key: 'name', width: 30 },
      { header: 'الوصف', key: 'description', width: 50 },
      { header: 'الوحدة التنظيمية', key: 'organizational_unit_name', width: 25 },
      { header: 'عدد المؤشرات', key: 'kpis_count', width: 15 },
      { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
    ],
    data: services,
  });
};

/**
 * Export Processes to Excel
 */
export const exportProcessesToExcel = (
  processes: any[],
  kpis: any[] = [],
  subProcesses: any[] = [],
  subProcessKpis: any[] = [],
  filename = 'العمليات',
) => {
  const sheets: SheetConfig[] = [
    {
      sheetName: 'العمليات',
      columns: [
        { header: 'اسم العملية', key: 'name', width: 30 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'الهدف الاستراتيجي', key: 'strategic_goal_title', width: 30 },
        { header: 'الوحدة التنظيمية', key: 'organizational_unit_names', width: 25 },
        { header: 'عدد العمليات الفرعية', key: 'sub_processes_count', width: 20 },
        { header: 'عدد المؤشرات', key: 'kpis_count', width: 15 },
      ],
      data: processes.map(p => ({
        ...p,
        organizational_unit_names: (p.organizational_units_details || [])
          .map((u: any) => u.name_ar || u.name)
          .join('، ') || '',
      })),
    },
  ];

  if (kpis.length > 0) {
    sheets.push({
      sheetName: 'مؤشرات العمليات',
      columns: [
        { header: 'العملية', key: 'process_name', width: 35 },
        { header: 'اسم المؤشر', key: 'name', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
        { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'وحدة القياس', key: 'unit', width: 15 },
        { header: 'التكرار', key: 'frequency_display', width: 15 },
        { header: 'الاتجاه', key: 'polarity_display', width: 15 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'معادلة القياس', key: 'measurement_formula', width: 30 },
      ],
      data: kpis.map(kpi => ({
        ...kpi,
        frequency_display: getFrequencyDisplay(kpi.frequency),
        polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
      })),
    });
  }

  if (subProcesses.length > 0) {
    sheets.push({
      sheetName: 'العمليات الفرعية',
      columns: [
        { header: 'العملية الرئيسية', key: 'process_name', width: 30 },
        { header: 'اسم العملية الفرعية', key: 'name', width: 30 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'الهدف الاستراتيجي', key: 'strategic_goal_title', width: 30 },
        { header: 'الوحدة التنظيمية', key: 'organizational_unit_name', width: 25 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'زمن العملية', key: 'duration_display', width: 15 },
        { header: 'عدد الموظفين', key: 'employees_count', width: 15 },
        { header: 'تكرار التنفيذ', key: 'execution_frequency', width: 15 },
        { header: 'تكلفة العملية', key: 'cost', width: 15 },
        { header: 'المسؤول عن الاعتماد', key: 'approval_person', width: 20 },
        { header: 'عدد الخطوات', key: 'steps_count', width: 15 },
        { header: 'عدد المؤشرات', key: 'kpis_count', width: 15 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
      ],
      data: subProcesses.map(sp => ({
        ...sp,
        organizational_unit_name: sp.organizational_unit_name || '',
      })),
    });
  }

  if (subProcessKpis.length > 0) {
    sheets.push({
      sheetName: 'مؤشرات العمليات الفرعية',
      columns: [
        { header: 'العملية الفرعية', key: 'sub_process_name', width: 35 },
        { header: 'اسم المؤشر', key: 'name', width: 40 },
        { header: 'الوصف', key: 'description', width: 50 },
        { header: 'القيمة المستهدفة', key: 'target_value', width: 15 },
        { header: 'القيمة القصوى المقبولة', key: 'worst_acceptable_value', width: 20 },
        { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
        { header: 'وحدة القياس', key: 'unit', width: 15 },
        { header: 'التكرار', key: 'frequency_display', width: 15 },
        { header: 'الاتجاه', key: 'polarity_display', width: 15 },
        { header: 'الوزن', key: 'weight', width: 10 },
        { header: 'معادلة القياس', key: 'measurement_formula', width: 30 },
      ],
      data: subProcessKpis.map(kpi => ({
        ...kpi,
        frequency_display: getFrequencyDisplay(kpi.frequency),
        polarity_display: kpi.polarity === 'positive' ? 'إيجابي' : 'سلبي',
      })),
    });
  }

  exportMultiSheetToExcel(filename, sheets);
};

/**
 * Export Unit Goals to Excel
 */
export const exportUnitGoalsToExcel = (unitGoals: any[], filename = 'أهداف_الوحدات') => {
  exportToExcel({
    filename,
    sheetName: 'أهداف الوحدات',
    columns: [
      { header: 'اسم الهدف', key: 'name', width: 30 },
      { header: 'الوصف', key: 'description', width: 50 },
      { header: 'الوحدة التنظيمية', key: 'unit_name', width: 25 },
      { header: 'الهدف الاستراتيجي', key: 'strategic_goal_name', width: 25 },
      { header: 'الوزن', key: 'weight', width: 10 },
      { header: 'نسبة الإنجاز', key: 'achievement_percentage', width: 15 },
      { header: 'حالة الإنجاز', key: 'achievement_status_display', width: 15 },
    ],
    data: unitGoals.map(goal => ({
      ...goal,
      achievement_status_display: getAchievementStatusDisplay(goal.achievement_status),
    })),
  });
};

// ============================================================
// Helper functions for display values
// ============================================================

const getGoalStatusDisplay = (status: string): string => {
  const statusMap: Record<string, string> = {
    draft: 'مسودة',
    pending: 'بانتظار الموافقة',
    approved: 'معتمد',
    rejected: 'مرفوض',
  };
  return statusMap[status] || status;
};

const getInitiativeStatusDisplay = (status: string): string => {
  const statusMap: Record<string, string> = {
    not_started: 'لم تبدأ',
    in_progress: 'قيد التنفيذ',
    completed: 'مكتملة',
    on_hold: 'متوقفة',
    cancelled: 'ملغاة',
  };
  return statusMap[status] || status;
};

const getFrequencyDisplay = (frequency: string): string => {
  const frequencyMap: Record<string, string> = {
    daily: 'يومي',
    weekly: 'أسبوعي',
    monthly: 'شهري',
    quarterly: 'ربع سنوي',
    semi_annual: 'نصف سنوي',
    annual: 'سنوي',
  };
  return frequencyMap[frequency] || frequency;
};

const getAchievementStatusDisplay = (status: string): string => {
  const statusMap: Record<string, string> = {
    excellent: 'ممتاز',
    good: 'جيد',
    acceptable: 'مقبول',
    poor: 'ضعيف',
  };
  return statusMap[status] || status;
};

export default {
  exportToExcel,
  exportGoalsToExcel,
  exportInitiativesToExcel,
  exportKPIsToExcel,
  exportKPIReadingsToExcel,
  exportEmployeesToExcel,
  exportOrganizationsToExcel,
  exportServicesToExcel,
  exportProcessesToExcel,
  exportUnitGoalsToExcel,
};
